// PT catch-up dashboard widget (CATCHUP_WIDGETS_PLAN.md Task 8). Self-fetching
// + auto-hiding, following the MissedDosesWidget pattern: renders null once the
// sweep finds no gap days or weekly shortfalls, or the settings toggle is off.

import { useEffect, useState } from "react";
import type { CatchUpResponse } from "@medassist/shared";
import { resolveCatchUpWidgets, resolveProactiveSettings } from "@medassist/shared";
import { getCatchUpData } from "../../../lib/catchUpFetch.js";
import { ApiError } from "../../../lib/api.js";
import { ActivityIcon } from "../../icons.js";
import { PtCatchUpModal } from "../../PtCatchUpModal.js";
import type { WidgetProps } from "../types.js";

export function PtCatchUpWidget({ patient, onError, onReload }: WidgetProps) {
  const [response, setResponse] = useState<CatchUpResponse | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const enabled = resolveCatchUpWidgets(
    resolveProactiveSettings(patient.proactiveSuggestions).catchUpWidgets
  ).pt;

  function refetch() {
    if (!enabled) return;
    getCatchUpData(patient.patientId)
      .then(setResponse)
      .catch((err) => onError(err instanceof ApiError ? err.message : "Couldn't load PT catch-up"));
  }

  useEffect(refetch, [patient.patientId, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!enabled) return null;

  const section = response?.sections.find((s) => s.kind === "pt");

  if (response === null) {
    return (
      <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="mb-2 flex items-center gap-2">
          <ActivityIcon size={16} className="text-amber-500 dark:text-amber-400" />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">PT catch-up</h2>
        </div>
        <p className="text-sm text-gray-400 dark:text-gray-500">Loading…</p>
      </section>
    );
  }

  if (!section || (section.gaps.length === 0 && section.weekly.length === 0)) return null;

  const total = section.gaps.length + section.weekly.length;

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <ActivityIcon size={16} className="text-amber-500 dark:text-amber-400" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">PT catch-up</h2>
        <button
          onClick={() => setReviewOpen(true)}
          className="ml-auto rounded-full bg-amber-100 dark:bg-amber-900/40 px-3 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800/40"
        >
          Review
        </button>
      </div>
      <p className="text-sm text-amber-700 dark:text-amber-300">
        {total} PT catch-up item{total !== 1 ? "s" : ""} this week
      </p>
      <ul className="mt-1 space-y-0.5">
        {section.gaps.slice(0, 3).map((g) => (
          <li key={`${g.programEventId}|${g.dayKey}`} className="truncate text-xs text-gray-500 dark:text-gray-400">
            {g.programLabel} — {g.dayKey}
          </li>
        ))}
        {section.gaps.length === 0 &&
          section.weekly.slice(0, 3).map((w) => (
            <li key={w.programEventId} className="truncate text-xs text-gray-500 dark:text-gray-400">
              {w.programLabel} — {w.loggedLastWeek}/{w.expectedPerWeek} this week
            </li>
          ))}
      </ul>
      {section.gaps.length > 3 && (
        <p className="text-xs text-gray-400 dark:text-gray-500">+{section.gaps.length - 3} more</p>
      )}
      {confirmation && <p className="mt-2 text-xs text-green-700 dark:text-green-300">{confirmation}</p>}

      {reviewOpen && (
        <PtCatchUpModal
          patientId={patient.patientId}
          gaps={section.gaps}
          weekly={section.weekly}
          onClose={() => {
            setReviewOpen(false);
            refetch();
          }}
          onLogged={(count) => {
            setConfirmation(`Logged ${count} ${count !== 1 ? "entries" : "entry"} ✓`);
            setTimeout(() => setConfirmation(null), 5000);
            onReload(); // refresh the shared events list + suggestion snapshot
          }}
        />
      )}
    </section>
  );
}
